import { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { getImageUrl } from '../../utils/imageUtils';
import { formatError } from '../../utils/errorUtils';
import {
  Building2,
  Copy,
  ExternalLink,
  ShieldCheck,
  Upload,
  FileText,
  AlertCircle,
  BadgeCheck,
  Globe,
  Lock,
  Loader2,
  Trash2,
  Plus
} from 'lucide-react';

const FRONTEND_URL = window.location.origin;
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { agency, updateAgencyProfile, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const brandImagesInputRef = useRef(null);
  const [uploadingBrand, setUploadingBrand] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [formData, setFormData] = useState({
    company_name: agency?.company_name || '',
    phone: agency?.phone || '',
    address: agency?.address || '',
    about: agency?.about || '',
    logo_url: agency?.logo_url || '',
    theme_color: agency?.theme_color || '#0EA5E9',
    brand_images: agency?.brand_images || [],
    custom_domain: agency?.custom_domain || '',
    domain_verified: agency?.domain_verified || false
  });

  const isPremium = agency?.subscription === 'premium' || agency?.subscription === 'enterprise';

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, WebP, or SVG image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload`, form, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      updateField('logo_url', res.data.url);
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || error.message;
      toast.error('Logo upload failed: ' + message);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const removeLogo = () => {
    updateField('logo_url', '');
    toast.info('Logo removed. Save to apply changes.');
  };

  const handleBrandImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (formData.brand_images.length + files.length > 5) {
      toast.error('You can upload up to 5 branding images');
      return;
    }

    setUploadingBrand(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API_URL}/api/upload`, form, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        return res.data.url;
      });

      const urls = await Promise.all(uploadPromises);
      updateField('brand_images', [...formData.brand_images, ...urls]);
      toast.success('Branding images uploaded!');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingBrand(false);
      if (brandImagesInputRef.current) brandImagesInputRef.current.value = '';
    }
  };

  const removeBrandImage = (index) => {
    const updated = formData.brand_images.filter((_, i) => i !== index);
    updateField('brand_images', updated);
  };

  const handleVerifyDomain = async () => {
    if (!formData.custom_domain) {
      toast.error('Please enter a domain name first');
      return;
    }
    setVerifyingDomain(true);
    // Simulate DNS check
    setTimeout(async () => {
      try {
        await updateAgencyProfile({
          ...formData,
          domain_verified: true
        });
        toast.success('Domain verified successfully!');
      } catch (error) {
        toast.error('Domain verification failed. Check DNS records.');
      } finally {
        setVerifyingDomain(false);
      }
    }, 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = { ...formData };
      // Only include custom_domain for premium users
      if (!isPremium) {
        delete submitData.custom_domain;
      }
      await updateAgencyProfile(submitData);
      toast.success('Profile updated!');
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || 'Failed to update profile';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const websiteUrl = `${FRONTEND_URL}/agency/${agency?.slug}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(websiteUrl);
    toast.success('Website URL copied!');
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Settings</h1>
        <p className="text-navy-600">Manage your agency profile and website settings</p>
      </div>

      {/* Website URL */}
      <Card className="mb-6" data-testid="website-url-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-sky-500" />
            Your Website URL
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={websiteUrl}
              readOnly
              className="bg-navy-50"
              data-testid="website-url-input"
            />
            <Button
              variant="outline"
              onClick={copyToClipboard}
              className="gap-2"
              data-testid="copy-url-btn"
            >
              <Copy className="w-4 h-4" />
              Copy
            </Button>
            <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="gap-2">
                <ExternalLink className="w-4 h-4" />
                Open
              </Button>
            </a>
          </div>
          <p className="text-sm text-navy-500 mt-2">
            Share this link with your clients to showcase your properties
          </p>
        </CardContent>
      </Card>

      {/* RERA Info */}
      <Card className="mb-6" data-testid="rera-info-card">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-green-800">RERA Verified</p>
              <p className="text-sm text-green-700">Registration: {agency?.rera_number}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Verification Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-sky-500" />
            Verification Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {agency?.verified ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <BadgeCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-900">Verified Partner</h3>
                <p className="text-green-700 text-sm">Your agency is fully verified and carries the trusted badge.</p>
              </div>
            </div>
          ) : agency?.verification_status === 'pending' ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-yellow-900">Verification Pending</h3>
                <p className="text-yellow-700 text-sm">
                  We are reviewing your documents. This usually takes 24-48 hours.
                </p>
                {agency.verification_requested_at && (
                  <p className="text-xs text-yellow-600 mt-1">
                    Submitted on: {new Date(agency.verification_requested_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Get Verified</h3>
                <p className="text-blue-700 text-sm mb-4">
                  Upload your RERA registration certificate or business license to get the Verified Partner badge.
                  Verified agencies get 3x more leads.
                </p>
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="max-w-xs bg-white"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;

                      const formData = new FormData();
                      formData.append('file', file);

                      setLoading(true);
                      try {
                        const token = localStorage.getItem('token');
                        await axios.post(`${API_URL}/api/agency/verify-request`, formData, {
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                          }
                        });
                        toast.success('Verification request submitted!');
                        window.location.reload();
                      } catch (err) {
                        const message = formatError(err.response?.data?.detail) || 'Upload failed. Please try again.';
                        toast.error(message);
                        console.error(err);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  />
                  <span className="text-xs text-navy-500">PDF, JPG or PNG (Max 5MB)</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Form */}
      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-sky-500" />
              Company Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => updateField('company_name', e.target.value)}
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  data-testid="phone-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Office Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  data-testid="address-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="about">About Company</Label>
                <Textarea
                  id="about"
                  value={formData.about}
                  onChange={(e) => updateField('about', e.target.value)}
                  className="min-h-[100px]"
                  data-testid="about-input"
                />
              </div>

              {/* Logo Upload */}
              <div className="space-y-2 md:col-span-2">
                <Label>Company Logo</Label>
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="w-24 h-24 bg-navy-50 rounded-xl border-2 border-dashed border-navy-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {formData.logo_url ? (
                      <img
                        src={getImageUrl(formData.logo_url)}
                        alt="Company Logo"
                        className="w-full h-full object-contain p-1"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Building2 className="w-8 h-8 text-navy-300" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="gap-2"
                        disabled={uploadingLogo}
                        onClick={() => logoInputRef.current?.click()}
                        data-testid="upload-logo-btn"
                      >
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Logo
                          </>
                        )}
                      </Button>
                      {formData.logo_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="gap-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={removeLogo}
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-navy-500">
                      JPG, PNG, WebP, or SVG. Max 5MB. Recommended: 400×400px.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="theme_color">Theme Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="theme_color"
                    type="color"
                    value={formData.theme_color}
                    onChange={(e) => updateField('theme_color', e.target.value)}
                    className="w-14 h-10 p-1 cursor-pointer"
                    data-testid="theme-color-input"
                  />
                  <Input
                    value={formData.theme_color}
                    onChange={(e) => updateField('theme_color', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Brand Showcase */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="w-5 h-5 text-sky-500" />
              Brand Showcase
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-navy-500 mb-2">
              Add images to your website's home page gallery. These can be photos of your team, office, or premium properties.
            </p>
            <div className="flex flex-wrap gap-4">
              {formData.brand_images.map((url, index) => (
                <div key={index} className="relative w-32 h-32 rounded-lg overflow-hidden border border-navy-100 group">
                  <img src={getImageUrl(url)} alt={`Brand ${index}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeBrandImage(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {formData.brand_images.length < 5 && (
                <button
                  type="button"
                  onClick={() => brandImagesInputRef.current?.click()}
                  className="w-32 h-32 rounded-lg border-2 border-dashed border-navy-200 flex flex-col items-center justify-center text-navy-400 hover:bg-navy-50 transition-colors"
                >
                  {uploadingBrand ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-6 h-6 mb-1" />
                      <span className="text-[10px] font-medium">Add Image</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <input
              ref={brandImagesInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleBrandImageUpload}
            />
          </CardContent>
        </Card>

        {/* Custom Domain (Premium) */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-sky-500" />
              Custom Domain
              {!isPremium && (
                <span className="flex items-center gap-1 text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  <Lock className="w-3 h-3" />
                  Premium
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="custom_domain">Domain Name</Label>
                <Input
                  id="custom_domain"
                  placeholder="properties.youragency.com"
                  value={formData.custom_domain}
                  onChange={(e) => updateField('custom_domain', e.target.value)}
                  disabled={!isPremium}
                  className={!isPremium ? 'opacity-50 cursor-not-allowed' : ''}
                  data-testid="custom-domain-input"
                />
                {isPremium && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={formData.domain_verified ? "outline" : "default"}
                      className={formData.domain_verified ? "text-green-600 bg-green-50" : ""}
                      onClick={handleVerifyDomain}
                      disabled={verifyingDomain}
                    >
                      {verifyingDomain ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : formData.domain_verified ? (
                        <BadgeCheck className="w-4 h-4 mr-2" />
                      ) : null}
                      {formData.domain_verified ? 'Verified' : 'Verify Domain'}
                    </Button>
                  </div>
                )}
              </div>
              {isPremium ? (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-xs text-blue-700">
                    <strong>Setup Instructions:</strong> Point a CNAME record for your domain to{' '}
                    <code className="bg-blue-100 px-1 py-0.5 rounded text-[10px]">
                      {window.location.hostname}
                    </code>
                    {' '}in your DNS settings. Changes may take up to 48 hours to propagate.
                  </p>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                  <p className="text-xs text-amber-700">
                    Upgrade to Premium to use your own custom domain and remove EstateCloud branding.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Button
          type="submit"
          className="w-full bg-navy-900 hover:bg-navy-800 h-11"
          disabled={loading}
          data-testid="save-settings-btn"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </form>
    </div >
  );
}
